export type AuthProvider = "email" | "google";

export const AUTH_PROVIDER_EMAIL_REQUIRED_MSG =
  "تم إنشاء هذا الحساب بالبريد الإلكتروني وكلمة المرور. يرجى تسجيل الدخول بالبريد وكلمة المرور.";

export const AUTH_PROVIDER_GOOGLE_REQUIRED_MSG =
  "تم إنشاء هذا الحساب عبر Google. يرجى المتابعة مع Google لتسجيل الدخول.";

export type StoredAuthEvidence = {
  loginMethod?: string | null;
  passwordHash?: string | null;
  email?: string | null;
};

export class AuthProviderMismatchError extends Error {
  readonly stored: AuthProvider;

  constructor(stored: AuthProvider) {
    super(authProviderMismatchMessage(stored));
    this.name = "AuthProviderMismatchError";
    this.stored = stored;
  }
}

export function normalizeAuthProvider(value: string | null | undefined): AuthProvider | null {
  if (!value) return null;
  const key = value.trim().toLowerCase();
  if (key === "email" || key === "password" || key === "email/password" || key === "email_password") {
    return "email";
  }
  if (key === "google") return "google";
  return null;
}

export function resolveStoredAuthProvider(user: StoredAuthEvidence): AuthProvider | null {
  const fromField = normalizeAuthProvider(user.loginMethod);
  if (fromField) return fromField;
  if (user.passwordHash) return "email";
  return null;
}

export function canAttachIncomingProvider(
  stored: AuthProvider | null,
  incoming: AuthProvider | null,
): boolean {
  if (!stored || !incoming) return true;
  return stored === incoming;
}

export function authProviderMismatchMessage(stored: AuthProvider): string {
  return stored === "google"
    ? AUTH_PROVIDER_GOOGLE_REQUIRED_MSG
    : AUTH_PROVIDER_EMAIL_REQUIRED_MSG;
}

export function assertIncomingProviderAllowed(
  stored: AuthProvider | null,
  incoming: AuthProvider | null,
): void {
  if (!canAttachIncomingProvider(stored, incoming)) {
    throw new AuthProviderMismatchError(stored!);
  }
}

export function isAuthProviderMismatchMessage(message: string | null | undefined): boolean {
  return (
    message === AUTH_PROVIDER_EMAIL_REQUIRED_MSG ||
    message === AUTH_PROVIDER_GOOGLE_REQUIRED_MSG
  );
}

export function passwordLoginRefusalMessage(
  user: StoredAuthEvidence | null | undefined,
): string | null {
  if (!user) return null;
  if (resolveStoredAuthProvider(user) === "google") {
    return AUTH_PROVIDER_GOOGLE_REQUIRED_MSG;
  }
  return null;
}

export function getAuthProviderMismatchMessage(error: unknown): string | null {
  const message = extractErrorMessage(error);
  return isAuthProviderMismatchMessage(message) ? message : null;
}

function extractErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }
  return "";
}
